import { useCallback, useState } from 'react';
import { reviewService } from '@/services/reviewService';
import { toReviewError, type ReviewError } from '../utils/errorMap';
import type {
  RestoreSubmissionBody,
  RestoreSubmissionResponse,
} from '../types/changelog';

interface UseRestoreSubmissionReturn {
  restore: (body: RestoreSubmissionBody) => Promise<RestoreSubmissionResponse>;
  restoring: boolean;
  error: ReviewError | null;
  reset: () => void;
}

/**
 * POST restore mutation. Symmetrical to `useUpdateSubmission` — same
 * error surface, same re-throw semantics. `body.change_log_id` is
 * either a numeric id or the string `'original'`; the caller decides.
 */
export function useRestoreSubmission(
  resource: string | undefined,
  id: number | string | undefined,
): UseRestoreSubmissionReturn {
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState<ReviewError | null>(null);

  const restore = useCallback(
    async (body: RestoreSubmissionBody): Promise<RestoreSubmissionResponse> => {
      if (!resource || id === undefined || id === null || id === '') {
        throw new Error('resource and id are required to restore a submission');
      }
      setRestoring(true);
      setError(null);
      try {
        return await reviewService.restoreSubmission(resource, id, body);
      } catch (err) {
        const mapped = toReviewError(err);
        setError(mapped);
        throw err;
      } finally {
        setRestoring(false);
      }
    },
    [resource, id],
  );

  const reset = useCallback(() => setError(null), []);

  return { restore, restoring, error, reset };
}
